import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AccountIntentTopic } from 'src/app/shared/dto';
import { IntentSourceEnum } from 'src/app/shared/enums';
import { CommonUtilService } from 'src/app/shared/services/CommonUtil.service';
import { ConvertedIntentTopicInterface } from '../../models/converted-intent-topic.interface';
import { FormChangeStateService } from 'src/app/shared/components';


@UntilDestroy()
@Component({
    selector: 'app-intent',
    templateUrl: './intent.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./intent.component.scss'],
    standalone: false
})
export class IntentComponent implements OnInit{
    @Input() disabled: boolean = false;
    @Input() set accountTopics(topics: AccountIntentTopic[]) {
        if (topics) {
            this.existingTopics = [...topics];
            this.existingTopicsAcc = topics.reduce((acc: Record<string, AccountIntentTopic>, topic: AccountIntentTopic) => ({
                ...acc,
                [topic.topic_id]: topic,
            }), {});
        }
    }
    @Input() source: IntentSourceEnum.BOMBORA | IntentSourceEnum.SALES_INTEL;
    @Input() plans = [];
    @Input() planPropertyValues;
    @Input() topics: ConvertedIntentTopicInterface[];
    @Input() isLoading: boolean;
    @Input() showGetRecommendations: boolean = false;
    /** Wizard mode: `topics` is a recommendation set; hides the main-view header and adds "Add all". */
    @Input() recommendationMode: boolean = false;

    @Output() listChange: EventEmitter<AccountIntentTopic[]> = new EventEmitter<AccountIntentTopic[]>();
    @Output() getRecommendations: EventEmitter<void> = new EventEmitter<void>();

    searchAvailableTopic = '';
    existingTopics: AccountIntentTopic[];
    existingTopicsAcc: Record<string, AccountIntentTopic>;
    searchYourTopic = '';
    filterIntentType: 'categories' | 'topics' = 'categories';
    collapsedCategories: Set<string> = new Set<string>();

    constructor(
        private readonly commonUtilService: CommonUtilService,
        private readonly toastr: ToastrService,
        private readonly formChangeStateService: FormChangeStateService
    ) {}
    
    ngOnInit(): void {
        if (this.disabled) this.formChangeStateService.setFormValidity(false);
    }

    addTopic(topic: ConvertedIntentTopicInterface): void {
        if (topic.category) {
            return;
        }

        const accountIntentTopic: AccountIntentTopic = {
            topic_id: topic.topic_id,
            topic_name: topic.topic_name,
            intent_source: this.source,
        }

        if (!this.existingTopicsAcc?.[topic.topic_id]) {
            if (this.existingTopics.length < this.planPropertyValues.maxTopics) {
                this.existingTopics = [...this.existingTopics, accountIntentTopic];
                this.existingTopicsAcc = {
                    ...this.existingTopicsAcc,
                    [topic.topic_id]: topic,
                }
                this.onListChange(this.existingTopics);

                this.commonUtilService.churnZeroEvents(`Portal - Account Settings - ${this.source === IntentSourceEnum.SALES_INTEL ? 'SI ' : ''}Intent Added`);
                return;
            }
            this.toastr.warning('Maximum of ' + this.planPropertyValues.maxTopics + ' topics can be selected.');
            return;
        }

        this.deleteTopic(accountIntentTopic);
    }

    deleteTopic(topic: AccountIntentTopic): void {
        this.existingTopics = this.existingTopics.filter(({ topic_id }: AccountIntentTopic) => topic_id !== topic.topic_id);
        const { [topic.topic_id]: _, ...rest } = this.existingTopicsAcc;
        this.existingTopicsAcc = rest;
        this.onListChange(this.existingTopics);
        this.commonUtilService.churnZeroEvents(`Portal - Account Settings - ${this.source === IntentSourceEnum.SALES_INTEL ? 'SI ' : ''}Intent Deleted`);
    }

    removeAll(): void {
        this.existingTopics = [];
        this.existingTopicsAcc = {};
        this.onListChange(this.existingTopics);
        this.commonUtilService.churnZeroEvents(`Portal - Account Settings - ${this.source === IntentSourceEnum.SALES_INTEL ? 'SI ' : ''}Intent Deleted`);
    }

    addAll(): void {
        if (this.disabled) {
            return;
        }

        const toAdd: ConvertedIntentTopicInterface[] = (this.topics || []).filter(
            (topic: ConvertedIntentTopicInterface) => !topic.category && !this.existingTopicsAcc?.[topic.topic_id]
        );

        // All-or-nothing: if the full set won't fit within the plan limit, add nothing and warn.
        if (this.existingTopics.length + toAdd.length > this.planPropertyValues.maxTopics) {
            this.toastr.warning('Maximum of ' + this.planPropertyValues.maxTopics + ' topics can be selected.');
            return;
        }

        let existing: AccountIntentTopic[] = [...this.existingTopics];
        let acc: Record<string, AccountIntentTopic> = { ...this.existingTopicsAcc };
        for (const topic of toAdd) {
            const accountIntentTopic: AccountIntentTopic = {
                topic_id: topic.topic_id,
                topic_name: topic.topic_name,
                intent_source: this.source,
            };
            existing = [...existing, accountIntentTopic];
            acc = { ...acc, [topic.topic_id]: accountIntentTopic };
        }

        this.existingTopics = existing;
        this.existingTopicsAcc = acc;
        this.onListChange(this.existingTopics);
        this.commonUtilService.churnZeroEvents(`Portal - Account Settings - ${this.source === IntentSourceEnum.SALES_INTEL ? 'SI ' : ''}Intent Added`);
    }

    private onListChange(topics: AccountIntentTopic[]): void {
        this.listChange.emit(topics);
    }

    onGetRecommendations(): void {
        this.getRecommendations.emit();
    }

    toggleCategory(category: string): void {
        // New Set reference so the (pure) collapsedCategoriesFilter pipe re-runs.
        const next: Set<string> = new Set(this.collapsedCategories);
        if (next.has(category)) {
            next.delete(category);
        } else {
            next.add(category);
        }
        this.collapsedCategories = next;
    }
}
